import {
  CompoundPathWithEdgeMap,
  CycleBoundaryDrawable,
  CycleSymbolDrawable,
  Edge,
  MeshPoint,
  SNAPPING_ERROR_TOLERANCE,
} from 'formwork-planner-lib'
import paper from 'paper/dist/paper-core'
import { CYCLE_COLOR_MAPPING, WALL_COLOR, WALL_STROKE_COLOR } from '../../../constants/colors'
import { PlanVisibilitySettings } from '../../../models/plan-visibility-settings'
import { WallModel } from '../../planner/model/WallModel'
import { parseSimplifiedWallMesh, serializeMesh } from '../../planner/model/MeshSerializer'
import { WallRenderService } from '../../planner/services/wall-render.service'
import { CYCLE_NUMBER_DISTANCE, CycleBoundaryLabel } from '../model/CycleBoundaryLabel'
import { CycleSymbolLabel } from '../model/CycleSymbolLabel'
import { CursorType, CycleSelectionService } from './cycle-selection.service'
import { InteractionState } from '../../planner/types/interactionState'
import { BehaviorSubject } from 'rxjs'
import { CycleDimensionsService } from './cycle-dimensions.service'
import { CycleDimensionValuesForCalculation } from '../../../models/cycleDimensions'

const DEFAULT_WALL_STYLE = {
  strokeColor: WALL_STROKE_COLOR,
  strokeWidth: 1,
  dashArray: [] as number[],
  fillRule: 'evenodd',
  fillColor: WALL_COLOR,
} as paper.Style

const DEFAULT_CYCLE_NUMBER = 1
const FLOOD_FILLING_POINT_DISTANCE = 1

export class CycleRenderService extends WallRenderService {
  private htmlBody: HTMLElement

  private cycleLayer: paper.Layer
  private cycleFillLayer: paper.Layer

  public cycleDimensionsService?: CycleDimensionsService
  public cycleSelectionService?: CycleSelectionService

  public snappingLines: paper.Path.Line[] = [] // TODO: Should be private

  public cycleBoundaryDrawables: CycleBoundaryDrawable[] = []
  public cycleSymbolsDrawable: CycleSymbolDrawable[] = [] // contains the cycle symbols of standalone Cycles

  public cycleBoundaryPreviewDrawable: CycleBoundaryDrawable | undefined

  public hoveredCycleBoundaryLabel?: CycleBoundaryLabel
  public hoveredCycleSymbolLabel?: CycleSymbolLabel

  public get allCycleBoundaryDrawables(): CycleBoundaryDrawable[] {
    return this.cycleBoundaryPreviewDrawable
      ? [...this.cycleBoundaryDrawables, this.cycleBoundaryPreviewDrawable]
      : this.cycleBoundaryDrawables
  }

  private cycleDimensionValuesForCalculation = new BehaviorSubject<
    CycleDimensionValuesForCalculation[]
  >([])

  public circumferenceSubject$ = this.cycleDimensionValuesForCalculation.asObservable()

  public constructor(
    model: WallModel,
    paperScope: paper.PaperScope,
    planVisibilitySettings: PlanVisibilitySettings,
    cycleBoundaryDrawables: CycleBoundaryDrawable[],
    cycleSymbolsDrawable: CycleSymbolDrawable[],
    cycleDimensionsService?: CycleDimensionsService,
    cycleSelectionService?: CycleSelectionService
  ) {
    super(model, paperScope, planVisibilitySettings)
    this.htmlBody = document.getElementsByTagName('body')[0]

    this.cycleDimensionsService = cycleDimensionsService
    this.cycleSelectionService = cycleSelectionService

    this.cycleBoundaryDrawables = cycleBoundaryDrawables
    this.cycleSymbolsDrawable = cycleSymbolsDrawable

    this.meshHighlightVisible = false
    this.angleLabelsVisible = false

    this.cycleFillLayer = this.createLayer(paperScope, 'CycleFillLayer')
    this.cycleLayer = this.createLayer(paperScope, 'CycleLayer')
    this.orderLayers()
  }

  public setMouseCursor(type: CursorType): void {
    if (this.htmlBody) {
      this.htmlBody.style.cursor = type
    }
  }

  public hoverCycleBoundaryLabel(cycleBoundaryLabel: CycleBoundaryLabel): void {
    this.hoveredCycleBoundaryLabel = cycleBoundaryLabel
    this.hoveredCycleSymbolLabel = undefined
    this.setMouseCursor(CursorType.POINTER)
    this.draw()
  }

  public hoverCycleSymbolLabel(cycleSymbolLabel: CycleSymbolLabel): void {
    this.hoveredCycleSymbolLabel = cycleSymbolLabel
    this.hoveredCycleBoundaryLabel = undefined
    this.setMouseCursor(CursorType.POINTER)
    this.draw()
  }

  public clearAllHoverStates(): void {
    this.hoveredCycleBoundaryLabel = undefined
    this.hoveredCycleSymbolLabel = undefined
    this.setMouseCursor(CursorType.DEFAULT)
    this.draw()
  }

  public setCycles(
    cycleBoundaryDrawables: CycleBoundaryDrawable[],
    cycleSymbolsDrawable: CycleSymbolDrawable[],
    cycleBoundaryPreviewDrawable?: CycleBoundaryDrawable
  ): void {
    this.cycleBoundaryDrawables = cycleBoundaryDrawables
    this.cycleSymbolsDrawable = cycleSymbolsDrawable
    this.cycleBoundaryPreviewDrawable = cycleBoundaryPreviewDrawable
    this.draw()
  }

  public draw(): void {
    if (this.cycleFillLayer) {
      this.cycleFillLayer.removeChildren()
    }
    if (this.cycleLayer) {
      this.cycleLayer.removeChildren()
    }

    const selectedCycleBoundaryId = this.cycleSelectionService?.selectedCycleBoundary?.id
    const clickedCycleBoundaryForDragId =
      this.cycleSelectionService?.clickedCycleBoundaryForDrag?.id

    this.cycleBoundaryDrawables.forEach((cycle) => {
      let boundaryState = InteractionState.ENABLED

      if (this.cycleSelectionService && selectedCycleBoundaryId === cycle.id) {
        boundaryState =
          this.cycleSelectionService.isLengthLabelSelectionOpen ||
          cycle.id === clickedCycleBoundaryForDragId
            ? InteractionState.DRAG
            : InteractionState.SELECTED
      } else if (this.hoveredCycleBoundaryLabel?.cycleBoundaryDrawable.id === cycle.id) {
        boundaryState = InteractionState.HOVER
      }

      let leftSymbolState = InteractionState.ENABLED
      let rightSymbolState = InteractionState.ENABLED

      const selectedCyclePosition = this.cycleSelectionService?.selectedCycleSymbol?.cyclePosition
      if (selectedCyclePosition) {
        if (cycle.getLeftPositionFromCenter(CYCLE_NUMBER_DISTANCE).equals(selectedCyclePosition)) {
          leftSymbolState = InteractionState.SELECTED
        }
        if (cycle.getRightPositionFromCenter(CYCLE_NUMBER_DISTANCE).equals(selectedCyclePosition)) {
          rightSymbolState = InteractionState.SELECTED
        }
      } else if (this.hoveredCycleSymbolLabel) {
        if (
          cycle
            .getLeftPositionFromCenter(CYCLE_NUMBER_DISTANCE)
            .equals(this.hoveredCycleSymbolLabel.position)
        ) {
          leftSymbolState = InteractionState.HOVER
        }
        if (
          cycle
            .getRightPositionFromCenter(CYCLE_NUMBER_DISTANCE)
            .equals(this.hoveredCycleSymbolLabel.position)
        ) {
          rightSymbolState = InteractionState.HOVER
        }
      }

      this.cycleLayer.addChild(
        new CycleBoundaryLabel(cycle, boundaryState, leftSymbolState, rightSymbolState)
      )
    })

    if (this.cycleBoundaryPreviewDrawable) {
      this.cycleLayer.addChild(
        new CycleBoundaryLabel(
          this.cycleBoundaryPreviewDrawable,
          InteractionState.PREVIEW,
          InteractionState.PREVIEW,
          InteractionState.PREVIEW
        )
      )
    }

    this.cycleSymbolsDrawable.forEach((symbol) => {
      let state = InteractionState.ENABLED
      if (
        this.cycleSelectionService &&
        this.cycleSelectionService.selectedCycleSymbol?.cyclePosition === symbol.position
      ) {
        state = InteractionState.SELECTED
      } else if (this.hoveredCycleSymbolLabel?.position.equals(symbol.position)) {
        state = InteractionState.HOVER
      }

      this.cycleLayer.addChild(new CycleSymbolLabel(symbol.position, symbol.cycleNumber, state))
    })

    const cycleBoundaries = this.allCycleBoundaryDrawables
    super.draw(cycleBoundaries, undefined)

    const cycleMeshPaths = this.processCycleMeshSegmentation(
      cycleBoundaries,
      this.cycleSymbolsDrawable
    )
    if (cycleMeshPaths) {
      this.calculateAndUpdateCycleDimensions(cycleMeshPaths)
      this.applyFloodFill(cycleMeshPaths)
    }

    this.orderLayers()
  }

  /**
   * Processes cycle boundaries and symbols to create isolated mesh segments.
   * This function performs the following operations:
   * 1. Splits edges at cycle boundary positions creating intermediate mesh points
   * 2. Creates separate mesh segments for each cycle area
   * 3. Associates cycle numbers with their corresponding mesh segments
   * 4. Handles both boundary-defined cycles and symbol-defined cycles
   *
   * @param cycleBoundaries - Array of drawable cycle boundary objects that define cycle separations
   * @param cycleSymbols - Array of drawable cycle symbol objects that define cycle numbers and positions
   * @returns CompoundPathWithEdgeMap[] - Array of compound paths representing the isolated cycle areas
   */
  private processCycleMeshSegmentation(
    cycleBoundaries: CycleBoundaryDrawable[],
    cycleSymbols: CycleSymbolDrawable[]
  ): CompoundPathWithEdgeMap[] | undefined {
    if (!this.path.children) {
      return
    }

    const cycleMesh = parseSimplifiedWallMesh(this.paperScope, serializeMesh(this.model.mesh))

    // for every cycle boundary the edges are split and "mock" edges are created
    for (const boundary of cycleBoundaries) {
      const boundaryPoint = boundary.getCenter()
      const edge = cycleMesh.findClosestEdge(boundaryPoint)

      if (!edge) {
        continue
      }

      const boundaryMeshEndPoint = new MeshPoint(boundaryPoint, cycleMesh)
      const boundaryMeshStartPoint = new MeshPoint(boundaryPoint, cycleMesh)

      const newStartEdge = new Edge(edge.startPoint, boundaryMeshEndPoint, edge.thickness)
      const newEndEdge = new Edge(boundaryMeshStartPoint, edge.endPoint, edge.thickness)

      const distanceToStart = newStartEdge.length()
      const distanceToEnd = newEndEdge.length()

      if (distanceToStart > SNAPPING_ERROR_TOLERANCE) {
        cycleMesh.points.add(boundaryMeshEndPoint)
        boundaryMeshEndPoint.edges.add(newStartEdge)
        edge.startPoint.edges.add(newStartEdge)
        edge.startPoint.edges.delete(edge)
        if (distanceToEnd <= SNAPPING_ERROR_TOLERANCE) {
          cycleMesh.points.delete(edge.endPoint)
        }
      }

      if (distanceToEnd > SNAPPING_ERROR_TOLERANCE) {
        cycleMesh.points.add(boundaryMeshStartPoint)
        boundaryMeshStartPoint.edges.add(newEndEdge)
        edge.endPoint.edges.add(newEndEdge)
        edge.endPoint.edges.delete(edge)
        if (distanceToStart <= SNAPPING_ERROR_TOLERANCE) {
          cycleMesh.points.delete(edge.startPoint)
        }
      }
    }

    // the mock edges are used to generate a closed edge outline for every cycle
    const cycleMeshPaths = cycleMesh
      .groupConnectedEdges(cycleBoundaries)
      .map((mesh) => mesh.generatePath())

    for (const boundary of cycleBoundaries) {
      const leftCycleMesh = cycleMeshPaths.find((path) =>
        path.contains(boundary.getLeftPositionFromCenter(FLOOD_FILLING_POINT_DISTANCE))
      )
      const rightCycleMesh = cycleMeshPaths.find((path) =>
        path.contains(boundary.getRightPositionFromCenter(FLOOD_FILLING_POINT_DISTANCE))
      )

      if (leftCycleMesh != null && Number.isNaN(+leftCycleMesh.data)) {
        leftCycleMesh.data = boundary.cycleSymbolLeft.cycleNumber.toString()
      }

      if (rightCycleMesh != null && Number.isNaN(+rightCycleMesh.data)) {
        rightCycleMesh.data = boundary.cycleSymbolRight.cycleNumber.toString()
      }
    }

    for (const symbol of cycleSymbols) {
      const currentCycleMesh = cycleMeshPaths.find((path) => path.contains(symbol.position))

      if (currentCycleMesh != null && Number.isNaN(+currentCycleMesh.data)) {
        currentCycleMesh.data = symbol.cycleNumber.toString()
      }
    }
    return cycleMeshPaths
  }

  /**
   * Creates the coloring for the cycles
   * @param cycleMeshPaths array of CompoundPathWithEdgeMap objects
   */
  private applyFloodFill(cycleMeshPaths: CompoundPathWithEdgeMap[]): void {
    // call this function after the cycleNumbers has been set

    cycleMeshPaths.forEach((path) => {
      const cycleNumber = Number.isNaN(+path.data) ? DEFAULT_CYCLE_NUMBER : path.data
      path.data = cycleNumber

      // the paths are colored according to the cycle number
      path.style = {
        ...DEFAULT_WALL_STYLE,
        fillRule: 'evenodd',
        fillColor: CycleRenderService.getCycleColor(cycleNumber),
      }
      this.cycleFillLayer.addChild(path)
    })
  }

  /**
   * Calculates and updates the dimensions (length, circumference, and area) for each cycle
   * based on the provided cycle mesh paths. The results are then emitted for further use.
   *
   * @param cycleMeshPaths - An array of paths representing the cycles with their respective edge maps.
   */
  private calculateAndUpdateCycleDimensions(cycleMeshPaths: CompoundPathWithEdgeMap[]): void {
    if (this.cycleDimensionsService) {
      // Values for the cycle dimensions overlay
      let length = new Map<number, number>()
      let circumference = new Map<number, number>()
      let area = new Map<number, number>()

      cycleMeshPaths.forEach((path) => {
        const cycleNumber = Number.isNaN(+path.data) ? DEFAULT_CYCLE_NUMBER : path.data
        const cycleLength = this.cycleDimensionsService?.getLength(
          path.curves.map((curve) => ({ length: curve.length, points: curve.points }))
        )

        length = this.addOrUpdateDimensionValuesForCalculation(
          +cycleNumber,
          cycleLength ?? 0,
          length
        )
        circumference = this.addOrUpdateDimensionValuesForCalculation(
          +cycleNumber,
          path.length,
          circumference
        )
        area = this.addOrUpdateDimensionValuesForCalculation(+cycleNumber, path.area, area)
      })

      const cycleDimensionValuesForCalculation: CycleDimensionValuesForCalculation[] = []
      const uniqueIds = Array.from(length.keys())
      uniqueIds.forEach((cycleNumber) => {
        cycleDimensionValuesForCalculation.push({
          cycleNumber,
          length: length.get(cycleNumber) ?? 0,
          circumference: circumference.get(cycleNumber) ?? 0,
          floorArea: area.get(cycleNumber) ?? 0,
        })
      })
      this.cycleDimensionValuesForCalculation.next(cycleDimensionValuesForCalculation)
    }
  }

  addOrUpdateDimensionValuesForCalculation(
    cycleNumber: number,
    value: number,
    dimensionMap: Map<number, number>
  ): Map<number, number> {
    if (dimensionMap.has(cycleNumber)) {
      const currentValue = dimensionMap.get(cycleNumber) ?? 0
      dimensionMap.set(cycleNumber, currentValue + value)
    } else {
      dimensionMap.set(cycleNumber, value)
    }
    return dimensionMap
  }

  public orderLayers(): void {
    if (this.cycleFillLayer) {
      this.cycleFillLayer.bringToFront()
    }
    if (this.cycleLayer) {
      this.cycleLayer.bringToFront()
    }
  }

  public static getCycleColor(cycleNumber: number): paper.Color {
    const maxCycleNumbers = Object.keys(CYCLE_COLOR_MAPPING).length
    return new paper.Color(CYCLE_COLOR_MAPPING[`${cycleNumber % maxCycleNumbers}`] ?? '')
  }
}
